const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** Allowed project types (matches server allowlist). */
export const PROJECT_TYPE_OPTIONS = [
  'Event',
  'Software',
  'Product',
  'Marketing',
  'Operations',
  'Compliance',
  'Legal',
  'R&D',
  'HR',
  'Finance',
  'Inventory',
] as const;

export type ProjectTypeOption = (typeof PROJECT_TYPE_OPTIONS)[number];

export interface ProjectCollaborator {
  user_id: number;
  username: string;
}

export type ProjectStatus = 'Active' | 'On Hold' | 'Closed';

export interface ProjectRow {
  id: number;
  name: string;
  project_types: string[];
  start_date: string | null;
  expected_end_date: string | null;
  created_by_user_id: number | null;
  created_by_username: string | null;
  status: ProjectStatus;
  /** Event-type projects only. */
  event_location: string | null;
  event_start_time: string | null;
  event_end_time: string | null;
  collaborators: ProjectCollaborator[];
}

export interface ProjectsListResponse {
  /** Active and On Hold projects the viewer owns or collaborates on. */
  my: ProjectRow[];
  /** Active and On Hold projects the viewer is not on. */
  active: ProjectRow[];
  closed: ProjectRow[];
  /** TFRI users only — every Active or On Hold project (kept for back-compat). */
  all?: ProjectRow[];
}

export async function fetchProjectsCategorized(userId: number): Promise<ProjectsListResponse> {
  const res = await fetch(`${API_BASE}/api/projects?userId=${encodeURIComponent(userId)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch projects');
  return {
    my: data.my ?? [],
    active: data.active ?? [],
    closed: data.closed ?? [],
    ...(Array.isArray(data.all) ? { all: data.all } : {}),
  };
}

export async function fetchProject(projectId: number, viewerUserId: number): Promise<ProjectRow> {
  const res = await fetch(
    `${API_BASE}/api/projects/${projectId}?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch project');
  return data as ProjectRow;
}

export async function createProject(payload: {
  name: string;
  project_types?: string[];
  start_date?: string | null;
  expected_end_date?: string | null;
  user_id: number;
  collaborator_user_ids?: number[];
  event_location?: string | null;
  event_start_time?: string | null;
  event_end_time?: string | null;
}): Promise<ProjectRow> {
  const res = await fetch(`${API_BASE}/api/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to create project');
  return data as ProjectRow;
}

export async function updateProject(
  projectId: number,
  payload: {
    user_id: number;
    name?: string;
    project_types?: string[];
    start_date?: string | null;
    expected_end_date?: string | null;
    collaborator_user_ids?: number[];
    status?: ProjectStatus;
    event_location?: string | null;
    event_start_time?: string | null;
    event_end_time?: string | null;
  }
): Promise<ProjectRow & { tasks_updated?: number }> {
  const res = await fetch(`${API_BASE}/api/projects/${projectId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update project');
  return data as ProjectRow & { tasks_updated?: number };
}

export async function deleteProject(projectId: number, userId: number): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}?user_id=${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  if (res.ok) return;
  const data = await res.json().catch(() => ({}));
  throw new Error((data as { error?: string }).error || 'Failed to delete project');
}

export type ProjectChecklistItemKind = 'checkbox' | 'section' | 'subsection';

export interface ProjectChecklistItemRow {
  id: number;
  checklist_id: number;
  label: string;
  is_checked: boolean;
  /** Major heading, sub-heading, or checkable line (default checkbox). */
  item_kind?: ProjectChecklistItemKind;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectChecklistRow {
  id: number;
  project_id: number;
  title: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  items: ProjectChecklistItemRow[];
}

export async function fetchProjectChecklists(
  projectId: number,
  viewerUserId: number
): Promise<ProjectChecklistRow[]> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/checklists?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch checklists');
  return (data as { checklists?: ProjectChecklistRow[] }).checklists ?? [];
}

export async function createProjectChecklist(
  projectId: number,
  payload: { user_id: number; title?: string }
): Promise<ProjectChecklistRow> {
  const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(projectId)}/checklists`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to create checklist');
  return data as ProjectChecklistRow;
}

export async function updateProjectChecklist(
  projectId: number,
  checklistId: number,
  payload: { user_id: number; title: string }
): Promise<ProjectChecklistRow> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/checklists/${encodeURIComponent(checklistId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update checklist');
  return data as ProjectChecklistRow;
}

export async function deleteProjectChecklist(
  projectId: number,
  checklistId: number,
  userId: number
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/checklists/${encodeURIComponent(checklistId)}?user_id=${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || 'Failed to delete checklist');
  }
}

export async function createChecklistItem(
  projectId: number,
  checklistId: number,
  payload: { user_id: number; label?: string; item_kind?: ProjectChecklistItemKind }
): Promise<ProjectChecklistItemRow> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/checklists/${encodeURIComponent(checklistId)}/items`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to add item');
  return data as ProjectChecklistItemRow;
}

export async function updateChecklistItem(
  projectId: number,
  checklistId: number,
  itemId: number,
  payload: { user_id: number; label?: string; is_checked?: boolean; item_kind?: ProjectChecklistItemKind }
): Promise<ProjectChecklistItemRow> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/checklists/${encodeURIComponent(checklistId)}/items/${encodeURIComponent(itemId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update item');
  return data as ProjectChecklistItemRow;
}

export async function deleteChecklistItem(
  projectId: number,
  checklistId: number,
  itemId: number,
  userId: number
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/checklists/${encodeURIComponent(checklistId)}/items/${encodeURIComponent(itemId)}?user_id=${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || 'Failed to delete item');
  }
}

export interface ProjectMemberRoleRow {
  user_id: number;
  username: string;
  is_owner: boolean;
  role: string;
  notes: string;
}

export async function fetchProjectMemberRoles(
  projectId: number,
  viewerUserId: number
): Promise<ProjectMemberRoleRow[]> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/member-roles?viewerUserId=${encodeURIComponent(viewerUserId)}`
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to fetch member roles');
  return (data as { members?: ProjectMemberRoleRow[] }).members ?? [];
}

export async function updateProjectMemberRole(
  projectId: number,
  memberUserId: number,
  payload: { user_id: number; role: string; notes: string }
): Promise<ProjectMemberRoleRow> {
  const res = await fetch(
    `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/member-roles/${encodeURIComponent(memberUserId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error || 'Failed to update member role');
  return data as ProjectMemberRoleRow;
}
